import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HiArrowRight } from "react-icons/hi";
import data from "../../data/index.json";
import { Section, SectionHeader, reveal } from "../../components";

/**
 * Services read as an index of capabilities — a numbered, rule-separated list.
 * A grid of identical cards flattened these into interchangeable tiles; a list
 * lets the eye scan titles first and drop into detail only where it wants to.
 */
const Services = () => {
  const { t } = useTranslation();
  const services = data.skills || [];

  return (
    <Section id="service" tone="paper">
      <SectionHeader
        index="02"
        label={t("Solutions")}
        title={
          <>
            {t("Enterprise-grade")} <em>{t("services")}</em>
          </>
        }
        lede={t(
          "Four practices, one delivery standard. Each engagement is scoped, staffed and shipped by the same senior team that designed it."
        )}
        aside={
          <a href="#contact" className="link-underline text-sm font-medium">
            {t("Discuss an engagement")}
            <HiArrowRight aria-hidden />
          </a>
        }
      />

      <ul className="mt-14 md:mt-20 border-t border-[color:var(--rule)]">
        {services.map((service, index) => (
          <motion.li
            key={service.id || index}
            {...reveal(index)}
            className="group border-b border-[color:var(--rule)]"
          >
            <a
              href="#contact"
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start py-8 md:py-10 transition-colors duration-200 hover:bg-paper-warm md:px-4 md:-mx-4"
            >
              <span className="label tabular text-gold-ink md:col-span-1 md:pt-2">
                {String(index + 1).padStart(2, "0")}
              </span>

              <div className="md:col-span-4 flex items-start gap-4">
                <img
                  src={service.src.replace("./", "/")}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  width="28"
                  height="28"
                  className="w-7 h-7 mt-1 object-contain opacity-70 shrink-0 transition-opacity duration-200 group-hover:opacity-100"
                  onError={(e) => {
                    e.target.src = "/img/code-svgrepo-com.svg";
                  }}
                />
                <h3 className="display-3">{t(service.title)}</h3>
              </div>

              <p className="md:col-span-6 text-[0.9375rem] leading-relaxed text-ink-soft max-w-measure">
                {t(service.description)}
              </p>

              <span className="md:col-span-1 flex md:justify-end md:pt-2">
                <HiArrowRight
                  aria-hidden
                  className="text-lg text-ink-faint transition-all duration-200 group-hover:text-ink group-hover:translate-x-1"
                />
              </span>
            </a>
          </motion.li>
        ))}
      </ul>
    </Section>
  );
};

export default Services;
